// src/pages/ChatSidebar.jsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import s from './Chats.module.css';
import { ReactComponent as ChatForm } from '../icons/create_chat_form.svg';
import { ReactComponent as ChatFormIcon } from '../icons/create_chat_icon.svg';
import { ReactComponent as SendMessageIcon } from '../icons/send_message_icon.svg';
import { SearchInput, Input, Textarea, ActionsMenu, UploadImage, Modal, EmployeeTaskStatsAvatar, Avatar } from './UI';
import { EMOJI_CLUSTER_RE, resolveEmojiSpriteStyle, useEmojiSpriteIndex } from '../utils/emojiSpriteIndex';
import { getChatDisplayTitle, isFavoritesChat } from '../utils/chatFavorites';
import { ReactComponent as FavoritesChatIcon } from '../icons/favorites_chat.svg';
import { createPortal } from 'react-dom';
import { apiFetch } from '../utils/auth';
import { API_URL } from '../config';

const NO_SPRITE_EMOJI = new Set(['📕', '📝', '📊', '🖥️', '📄', '🗂️', '📎']);

export function FavoritesChatAvatar({ className = '', size = 55 }) {
  return (
    <div
      className={[s.favoritesChatAvatar, className].filter(Boolean).join(' ')}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <FavoritesChatIcon className={s.favoritesChatAvatarIcon} />
    </div>
  );
}

function normalizeChatType(chat) {
  const t = chat?.type ?? chat?.chat_type;
  if (typeof t === 'string') return t.toLowerCase();
  if (t && typeof t === 'object') {
    return String(t.value || t.name || 'group').toLowerCase();
  }
  return 'group';
}

function getInitialsFromName(name) {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return parts[0].slice(0, 2).toUpperCase();
}

export function ChatCounterpartyAvatar({
  chat,
  getPeerEmployeeId,
  employeeTaskStatsMap,
  resolveImageUrl,
  size = 48,
  onClick,
  className = '',
  favoritesClassName = '',
  imgClassName = '',
}) {
  if (!chat || isFavoritesChat(chat)) {
    return <FavoritesChatAvatar size={size} className={favoritesClassName} />;
  }

  const type = normalizeChatType(chat);
  const isPrivate = type === 'private' || Boolean(chat.__virtual);
  const peerId = isPrivate && typeof getPeerEmployeeId === 'function'
    ? getPeerEmployeeId(chat)
    : null;

  const avatarUrl = resolveImageUrl?.(chat.avatar_url) || null;
  const chatTitle = getChatDisplayTitle(chat);
  const handleClick = onClick
    ? (e) => {
      e?.stopPropagation?.();
      onClick(e);
    }
    : undefined;

  if (peerId) {
    return (
      <EmployeeTaskStatsAvatar
        employeeId={peerId}
        stats={employeeTaskStatsMap?.get(peerId)}
        fetchStats={false}
        avatar={chat.avatar_url || undefined}
        size={size}
        onClick={handleClick}
        className={className}
        alt={chatTitle}
        initials={getInitialsFromName(chatTitle)}
      />
    );
  }

  return (
    <Avatar
      src={avatarUrl}
      name={chatTitle}
      alt={chatTitle}
      size={size}
      className={imgClassName}
      onClick={handleClick}
      style={handleClick ? { cursor: 'pointer' } : undefined}
    />
  );
}

export function ChatSenderAvatar({
  employeeId,
  avatarUrl,
  employeeTaskStatsMap,
  size = 48,
  onClick,
  className = '',
  alt = '',
}) {
  const id = Number(employeeId);
  if (!Number.isFinite(id) || id <= 0) return null;

  return (
    <EmployeeTaskStatsAvatar
      employeeId={id}
      stats={employeeTaskStatsMap?.get(id)}
      fetchStats={false}
      avatar={avatarUrl || undefined}
      size={size}
      onClick={onClick}
      className={className}
      alt={alt}
      initials={getInitialsFromName(alt)}
    />
  );
}

// Презентационная левая колонка: фильтры, список чатов, FAB, модалка создания группы/рангового
export default function ChatSidebar({
  role,
  canManageChats = false,
  canDeleteChats = false,
  filter,
  onChangeFilter,

  // 🔎 поиск по чатам
  showChatSearch,
  setShowChatSearch,
  chatSearchQuery,
  setChatSearchQuery,
  messageSearchItems,
  messageSearchLoading,
  messageSearchHasMore,
  messageSearchLoadingMore,
  onLoadMoreMessageSearch,

  isForwardSelectMode,
  forwardTargets,
  forwardCommentText,
  setForwardCommentText,
  onCancelForwardSelect,
  onToggleForwardTarget,
  onSubmitForward,

  filteredChats,
  chatDraftsByChatId,
  currentChatId,
  onSelectChat,
  onCreatePrivateFromVirtual,

  resolveImageUrl,
  formatTime,
  hasMsg,

  editMenuChatId,
  setEditMenuChatId,
  menuContainerRef,
  onOpenEditAvatar,
  onDeleteChat,

  onOpenCreateModal,

  // ===== РЕДАКТИРОВАНИЕ ЧАТА =====
  onOpenEditChat,
  isEditOpen,
  setEditOpen,
  editChatName,
  setEditChatName,
  editParticipants,
  onEditParticipantsChange,
  onSaveEditChat,
  myEmployeeId,
  editAvatarInputRef,
  onEditAvatarChange,
  editAvatarPreviewUrl,
  // ▼ для меню ролей участника в «Редактировать чат»
  roleMenuFor,
  setRoleMenuFor,
  toggleChatAdmin,
  editChatId,

  // Модалка создания.
  isCreateOpen,
  setCreateOpen,
  newChatName,
  setNewChatName,
  createAvatarInputRef,
  newChatAvatarPreviewUrl,
  onNewChatAvatarChange,
  selectedParticipants,
  onCreateParticipantsChange,
  precheck,
  disabledReason,
  onCreateChat,

  // NEW: поддержка ранговых чатов
  chatType,          // 'group' | 'rank'
  setChatType,
  rankOptions,       // список доступных рангов (string[])
  rankValues,        // массив выбранных рангов
  setRankValues,     // сеттер массива

  //  индикатор загрузки списка
  loading,
  getChatPresence,
  onAvatarClick,
  getPeerEmployeeId,
  employeeTaskStatsMap,

  // просмотр участников группового/рангового чата
  isMembersOpen,
  setIsMembersOpen,
  membersChatTitle,
  membersLoading,
  membersList,
}) {
  const filtersRef = React.useRef(null);
  const dragStateRef = React.useRef({
    isDown: false,
    startX: 0,
    scrollLeft: 0,
  });
  const emojiSpriteIndex = useEmojiSpriteIndex();
  const normalizedSearchQuery = (chatSearchQuery || '').trim();
  const showExtendedSearch = normalizedSearchQuery.length >= 2 && !isForwardSelectMode;
  const visibleChats = filteredChats;
  const hasChatResults = visibleChats.length > 0;
  const hasMessageResults = (messageSearchItems?.length || 0) > 0;
  const messageLoadMoreRef = React.useRef(null);

  const renderAppleEmojiInline = React.useCallback((text, keyBase = 'sidebar') => {
    const source = String(text || '');
    if (!source) return null;
    const parts = [];
    let lastIndex = 0;
    let match;
    let emojiIdx = 0;
    EMOJI_CLUSTER_RE.lastIndex = 0;

    while ((match = EMOJI_CLUSTER_RE.exec(source)) !== null) {
      const emojiText = match[0];
      const emojiStart = match.index;
      if (emojiStart > lastIndex) parts.push(source.slice(lastIndex, emojiStart));

      const emojiSpriteStyle = resolveEmojiSpriteStyle(emojiSpriteIndex, emojiText);
      if (emojiSpriteStyle && !NO_SPRITE_EMOJI.has(emojiText)) {
        parts.push(
          <span
            key={`${keyBase}-emoji-${emojiIdx}-${emojiStart}`}
            role="img"
            aria-label={emojiText}
            className={s.inlineAppleEmojiSprite}
            style={emojiSpriteStyle}
          />
        );
      } else {
        parts.push(emojiText);
      }

      lastIndex = emojiStart + emojiText.length;
      emojiIdx += 1;
    }

    if (lastIndex < source.length) parts.push(source.slice(lastIndex));
    return parts;
  }, [emojiSpriteIndex]);

  const onFiltersMouseDown = (e) => {
    const el = filtersRef.current;
    if (!el) return;

    dragStateRef.current.isDown = true;
    dragStateRef.current.startX = e.pageX - el.offsetLeft;
    dragStateRef.current.scrollLeft = el.scrollLeft;

    el.classList.add(s.filtersDragging);
  };

  const onFiltersMouseLeave = () => {
    const el = filtersRef.current;
    dragStateRef.current.isDown = false;
    el?.classList.remove(s.filtersDragging);
  };

  const onFiltersMouseUp = () => {
    const el = filtersRef.current;
    dragStateRef.current.isDown = false;
    el?.classList.remove(s.filtersDragging);
  };

  const onFiltersMouseMove = (e) => {
    const el = filtersRef.current;
    if (!el || !dragStateRef.current.isDown) return;

    e.preventDefault();
    const x = e.pageX - el.offsetLeft;
    const walk = x - dragStateRef.current.startX;
    el.scrollLeft = dragStateRef.current.scrollLeft - walk;
  };

  React.useEffect(() => {
    if (!editMenuChatId) return undefined;
    const handleOutsideClick = (e) => {
      const menuRoot = menuContainerRef?.current;
      if (menuRoot && !menuRoot.contains(e.target)) {
        setEditMenuChatId(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [editMenuChatId, menuContainerRef, setEditMenuChatId]);

  React.useEffect(() => {
    if (!showExtendedSearch || !hasMessageResults || !messageSearchHasMore) return undefined;
    if (messageSearchLoading || messageSearchLoadingMore) return undefined;
    if (typeof onLoadMoreMessageSearch !== 'function') return undefined;

    const sentinel = messageLoadMoreRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries?.[0];
        if (!entry?.isIntersecting) return;
        onLoadMoreMessageSearch();
      },
      { root: null, rootMargin: '120px 0px 120px 0px', threshold: 0.01 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    hasMessageResults,
    messageSearchHasMore,
    messageSearchLoading,
    messageSearchLoadingMore,
    onLoadMoreMessageSearch,
    showExtendedSearch,
  ]);

  return (
    <>
      {/* Фильтры */}
      <div className={s.filtersRow}>
        <div
          ref={filtersRef}
          className={s.filters}
          onMouseDown={onFiltersMouseDown}
          onMouseLeave={onFiltersMouseLeave}
          onMouseUp={onFiltersMouseUp}
          onMouseMove={onFiltersMouseMove}>
          <button
            type="button"
            onClick={() => onChangeFilter('all')}
            className={`${s.segbtn} ${filter === 'all' ? s.segbtnActive : ''}`}
          >
            Все
          </button>

          <button
            type="button"
            onClick={() => onChangeFilter('private')}
            className={`${s.segbtn} ${filter === 'private' ? s.segbtnActive : ''}`}
          >
            Личные
          </button>

          <button
            type="button"
            onClick={() => onChangeFilter('group')}
            className={`${s.segbtn} ${filter === 'group' ? s.segbtnActive : ''}`}
          >
            Группы
          </button>

        </div>

        {isForwardSelectMode && (
          <button
            type="button"
            className={s.forwardClose}
            aria-label="Отменить пересылку"
            title="Отменить пересылку"
            onClick={onCancelForwardSelect}
          >
            ×
          </button>
        )}
      </div>

      <div className={s.searchInputWrap}>
        <SearchInput
          value={chatSearchQuery}
          onChange={(e) => setChatSearchQuery(e.target.value)}
          placeholder="Поиск..."
          ariaLabel="Поиск чатов"
        />
      </div>

      {/* Список чатов */}
      {loading ? (
        <div className={s.card}>Загрузка чатов…</div>
      ) : visibleChats.length === 0 && (!showExtendedSearch || (!messageSearchLoading && !(messageSearchItems?.length))) ? (
        <div className={s.card}>
          {showExtendedSearch ? 'Ничего не найдено.' : 'У вас пока нет чатов.'}
        </div>
      ) : (
        <>
          {showExtendedSearch && hasChatResults && (
            <div className={s.card} style={{ marginBottom: 8, paddingTop: 8, paddingBottom: 8 }}>
              Чаты и люди
            </div>
          )}

          {(!showExtendedSearch || hasChatResults) && (
            <ul className={`${s.chatList} ${s.listEdgeToEdge}`}>
              {visibleChats.map((chat) => {
                const draftRaw = chatDraftsByChatId?.[String(chat.id)] || '';
                const draftText = typeof draftRaw === 'string' ? draftRaw.replace(/\s+/g, ' ').trim() : '';
                const hasDraft = draftText.length > 0;
                const isCurrentChat = String(currentChatId) === String(chat.id);
                const showDraftPreview = hasDraft && !isCurrentChat;
                const draftPreviewLimit = 44;
                const draftPreview = showDraftPreview && draftText.length > draftPreviewLimit
                  ? `${draftText.slice(0, draftPreviewLimit)}…`
                  : draftText;
                return (
                  <li
                    key={chat.id}
                    onClick={() => {
                      if (isForwardSelectMode) {
                        onToggleForwardTarget(chat.id);
                        return;
                      }
                      if (chat.__virtual) {
                        onCreatePrivateFromVirtual(chat);
                      } else {
                        setEditMenuChatId(null);
                        onSelectChat(chat.id);
                      }
                    }}
                    className={`${s.chatListItem} ${currentChatId === chat.id ? s.chatListItemActive : ""} ${isForwardSelectMode && forwardTargets.includes(chat.id) ? s.chatListItemForwardSelected : ""}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, paddingTop: 12, paddingBottom: 12, position: 'relative' }}
                  >
                    {/* Аватар чата слева */}
                    <div className={s.avatarWrap}>
                      <ChatCounterpartyAvatar
                        chat={chat}
                        getPeerEmployeeId={getPeerEmployeeId}
                        employeeTaskStatsMap={employeeTaskStatsMap}
                        resolveImageUrl={resolveImageUrl}
                        size={55}
                        imgClassName={s.chatAvatar}
                        onClick={() => {
                          if (typeof onAvatarClick === 'function') onAvatarClick(chat);
                        }}
                      />

                      {(() => {
                        const presence = typeof getChatPresence === 'function' ? getChatPresence(chat) : null;
                        if (!presence?.show) return null;
                        return (
                          <span
                            className={`${s.statusDot} ${presence.online ? s.statusOnline : s.statusOffline}`}
                            aria-label={presence.online ? 'Онлайн' : 'Оффлайн'}
                            title={presence.online ? 'Онлайн' : 'Оффлайн'}
                          />
                        );
                      })()}
                    </div>


                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          className={`${hasMsg(chat) ? '' : s.muted}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}
                        >
                          <span
                            style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                          >
                            {getChatDisplayTitle(chat)}
                          </span>
                          {Boolean(chat.is_muted) && (
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                              aria-label="Уведомления отключены"
                              title="Уведомления отключены"
                              style={{ flex: '0 0 auto' }}
                            >
                              <path
                                d="M22 8.99993L16 14.9999M16 8.99993L22 14.9999M9.63432 4.36561L6.46863 7.5313C6.29568 7.70425 6.2092 7.79073 6.10828 7.85257C6.01881 7.9074 5.92127 7.9478 5.81923 7.9723C5.70414 7.99993 5.58185 7.99993 5.33726 7.99993H3.6C3.03995 7.99993 2.75992 7.99993 2.54601 8.10892C2.35785 8.20479 2.20487 8.35777 2.10899 8.54594C2 8.75985 2 9.03987 2 9.59993V14.3999C2 14.96 2 15.24 2.10899 15.4539C2.20487 15.6421 2.35785 15.7951 2.54601 15.8909C2.75992 15.9999 3.03995 15.9999 3.6 15.9999H5.33726C5.58185 15.9999 5.70414 15.9999 5.81923 16.0276C5.92127 16.0521 6.01881 16.0925 6.10828 16.1473C6.2092 16.2091 6.29568 16.2956 6.46863 16.4686L9.63431 19.6342C10.0627 20.0626 10.2769 20.2768 10.4608 20.2913C10.6203 20.3038 10.7763 20.2392 10.8802 20.1175C11 19.9773 11 19.6744 11 19.0686V4.9313C11 4.32548 11 4.02257 10.8802 3.88231C10.7763 3.76061 10.6203 3.69602 10.4608 3.70858C10.2769 3.72305 10.0627 3.93724 9.63432 4.36561Z"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          )}
                        </div>

                        {/* ✎ кнопка — только для тех у кого есть доступ, для групповых И ранговых чатов */}
                        {(canManageChats || chat.is_chat_admin)
                          && !isFavoritesChat(chat)
                          && (chat.type === 'group' || chat.type === 'rank') && (
                            <ActionsMenu
                              open={editMenuChatId === chat.id}
                              containerRef={editMenuChatId === chat.id ? menuContainerRef : null}
                              wrapClassName={s.editWrap}
                              buttonClassName={s.editBtn}
                              title="Действия с чатом"
                              onToggle={(e) => {
                                e.stopPropagation();
                                setEditMenuChatId((prev) => (prev === chat.id ? null : chat.id));
                              }}
                              items={[
                                {
                                  key: 'edit',
                                  label: 'Редактировать',
                                  onClick: () => {
                                    setEditMenuChatId(null);
                                    onOpenEditChat(chat);
                                  },
                                },
                                {
                                  key: 'delete',
                                  label: 'Удалить чат',
                                  danger: true,
                                  onClick: () => {
                                    setEditMenuChatId(null);
                                    onDeleteChat(chat.id);
                                  },
                                },
                              ]}
                            />
                          )}

                        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#000000', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {formatTime(chat.last_message_time)}
                        </span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                        <span
                          className={`${hasMsg(chat) ? '' : s.muted}`}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}
                        >
                          {showDraftPreview ? (
                            <>
                              <span style={{ color: '#ff1f1f', fontWeight: 600 }}>Черновик: </span>
                              {renderAppleEmojiInline(draftPreview, `draft-${chat.id}`)}
                            </>
                          ) : (
                            renderAppleEmojiInline(
                              chat.last_message_text || (chat.last_message_time ? '📎 вложение' : 'Сообщений пока нет'),
                              `preview-${chat.id}`
                            )
                          )}
                        </span>
                        {chat.reaction_unread ? (
                          <span
                            className={s.chatListReactionBadge}
                            title="Реакция на ваше сообщение"
                            aria-label="Реакция на ваше сообщение"
                          >
                            <svg
                              className={s.reactionJumpHeartOutline}
                              viewBox="0 0 24 24"
                              aria-hidden="true"
                            >
                              <path
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="1.4"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
                              />
                            </svg>
                          </span>
                        ) : null}
                        {chat.unread_count > 0 && (
                          <span style={{
                            width: 15,
                            height: 15,
                            padding: '2px',
                            borderRadius: 10,
                            background: '#BD23DB',
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 500,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            {chat.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {showExtendedSearch && (messageSearchLoading || hasMessageResults) && (
            <>
              <div className={s.card} style={{ marginTop: 8, marginBottom: 8, paddingTop: 8, paddingBottom: 8 }}>
                Сообщения
              </div>

              {messageSearchLoading ? (
                <div className={s.card}>Ищем по сообщениям…</div>
              ) : (hasMessageResults ? (
                <>
                  <ul className={`${s.chatList} ${s.listEdgeToEdge}`}>
                    {messageSearchItems.map((item) => (
                      <li
                        key={`msg-search-${item.chat_id}-${item.message_id}`}
                        className={s.chatListItem}
                        style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingTop: 10, paddingBottom: 10 }}
                        onClick={() => {
                          setEditMenuChatId(null);
                          onSelectChat(item.chat_id, item.message_id);
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                          <span
                            style={{
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              minWidth: 0,
                            }}
                          >
                            {item.chat_name || 'Чат'}
                          </span>
                          <span className={s.muted} style={{ marginLeft: 'auto', fontSize: 11, whiteSpace: 'nowrap' }}>
                            {formatTime(item.timestamp)}
                          </span>
                        </div>

                        <div className={s.muted} style={{ fontSize: 12 }}>
                          {item.sender_name || 'Пользователь'}
                        </div>

                        <div
                          style={{
                            fontSize: 13,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {renderAppleEmojiInline(item.content_excerpt || '', `search-msg-${item.message_id}`)}
                        </div>
                      </li>
                    ))}
                  </ul>

                  {messageSearchHasMore && <div ref={messageLoadMoreRef} style={{ height: 1 }} aria-hidden="true" />}
                  {messageSearchLoadingMore && (
                    <div className={s.card} style={{ marginTop: 8 }}>
                      Подгружаем сообщения…
                    </div>
                  )}
                </>
              ) : null)}
            </>
          )}
        </>
      )}

      {/* FAB — создать чат (только для тех у кого есть доступ) */}
      {canManageChats && !isForwardSelectMode && (
        <button
          className={s.fab}
          onClick={onOpenCreateModal}
          aria-label="Создать чат"
          type="button"
        >
          <span className={s.fabSvgWrap} aria-hidden="true">
            <ChatForm className={s.fabBubble} />
            <ChatFormIcon className={s.fabInner} />
          </span>
        </button>
      )}

      {/* Поле комментария к пересылке (FAB — отдельно, как раньше) */}
      {isForwardSelectMode && (
        <div className={s.forwardComposerInputStrip}>
          <Textarea
            wrapperClassName={s.forwardComposerTextareaWrap}
            className={`${s.sendTextarea} ${s.forwardComposerTextarea}`}
            rows={2}
            value={forwardCommentText}
            onChange={(e) => setForwardCommentText(e.target.value)}
            placeholder="Сообщение…"
            ariaLabel="Комментарий к пересылке"
            resize="none"
          />
        </div>
      )}

      {/* FAB — отправить пересылаемое сообщение в выбранные чаты */}
      {isForwardSelectMode && (
        <button
          type="button"
          className={`${s.fabSend} ${forwardTargets.length === 0 ? s.fabSendDisabled : ''}`}
          onClick={onSubmitForward}
          aria-label="Отправить пересланное сообщение"
          disabled={forwardTargets.length === 0}
        >
          <SendMessageIcon className={s.fabSendIcon} />
        </button>
      )}

      {/* Модалка создания чата — для тех у кого есть доступ */}
      {canManageChats && isCreateOpen && (
        <Modal
          title="Создать чат"
          onClose={() => setCreateOpen(false)}
          onCancel={() => setCreateOpen(false)}
          onSubmit={onCreateChat}
          submitLabel="Создать"
          submitType="submit"
          formId="chat-create-form"
          submitDisabled={(chatType === 'rank' && (rankValues?.length || 0) === 0) || precheck === false}
          cardClassName={s.chatEditorModalCard}
          bodyClassName={s.chatEditorModalBody}
        >
          <form id="chat-create-form" onSubmit={onCreateChat} className={s.chatEditorForm}>
            <div className={`${s.segmented} ${s.chatEditorSegmented}`} role="tablist" aria-label="Тип чата">
              <button
                type="button"
                className={`${s.segbtn} ${chatType === 'group' ? s.segbtnActive : ''}`}
                onClick={() => setChatType('group')}
              >Групповой</button>
              <button
                type="button"
                className={`${s.segbtn} ${chatType === 'rank' ? s.segbtnActive : ''}`}
                onClick={() => setChatType('rank')}
              >Ранговый</button>
            </div>

            <UploadImage
              inputRef={createAvatarInputRef}
              onChange={onNewChatAvatarChange}
              previewUrl={newChatAvatarPreviewUrl}
              accept="image/*"
            />

            <label>Название <span style={{ color: "rgb(192, 16, 72)", fontWeight: "400" }}>*</span></label>
            <Input
              value={newChatName}
              onChange={(e) => setNewChatName(e.target.value)}
              placeholder={chatType === 'rank' ? 'Введите название рангового чата' : 'Введите название группы'}
              ariaLabel="Название"
              required
            />

            {chatType === 'rank' && (
              <>
                <label>Ранги</label>
                <select
                  className={s.input}
                  multiple
                  size={Math.min(8, Math.max(4, (rankOptions?.length || 0)))}
                  value={rankValues}
                  onChange={(e) => {
                    const vals = Array.from(e.target.selectedOptions).map(o => o.value);
                    setRankValues(vals);
                  }}
                  required
                >
                  {rankOptions.map(r => (
                    <option key={r.id} value={r.id}>
                      {(r.alt_name && r.alt_name.trim()) ? r.alt_name : r.name}
                    </option>
                  ))}
                </select>
                <p className={s.rankHint}>В чат автоматически добавятся все сотрудники с любым из выбранных рангов.</p>
              </>
            )}

            {chatType === 'group' && (
              <>
                <label>Участники <span style={{ color: "rgb(192, 16, 72)", fontWeight: "400" }}>*</span></label>
                <ChatParticipantPicker
                  selected={selectedParticipants}
                  onChange={onCreateParticipantsChange}
                  excludeEmployeeId={myEmployeeId}
                  resolveImageUrl={resolveImageUrl}
                  mode="create"
                />
                {precheck === false && (
                  <div className={s.inlineError}>
                    {disabledReason}
                  </div>
                )}
              </>
            )}
          </form>
        </Modal>
      )}

      {/* Модалка редактирования чата (групповой/ранговый) */}
      {isEditOpen && (
        <Modal
          title="Редактировать чат"
          onClose={() => setEditOpen(false)}
          onCancel={() => setEditOpen(false)}
          onSubmit={onSaveEditChat}
          submitLabel="Сохранить"
          cardClassName={s.chatEditorModalCard}
          bodyClassName={s.chatEditorModalBody}
        >
          <div className={s.chatEditorForm}>
            <UploadImage
              inputRef={editAvatarInputRef}
              onChange={onEditAvatarChange}
              previewUrl={editAvatarPreviewUrl}
              accept="image/*"
            />
            <label>Название</label>
            <Input
              value={editChatName}
              onChange={(e) => setEditChatName(e.target.value)}
              placeholder="Введите название"
              ariaLabel="Название"
            />

            <label>Участники</label>
            <ChatParticipantPicker
              selected={editParticipants}
              onChange={onEditParticipantsChange}
              currentEmployeeId={myEmployeeId}
              resolveImageUrl={resolveImageUrl}
              mode="edit"
              editChatId={editChatId}
              roleMenuFor={roleMenuFor}
              setRoleMenuFor={setRoleMenuFor}
              onToggleChatAdmin={toggleChatAdmin}
            />
          </div>
        </Modal>
      )}

      {isMembersOpen && (
        <Modal
          title="Участники чата"
          onClose={() => setIsMembersOpen(false)}
          footer={false}
          cardClassName={s.chatEditorModalCard}
          bodyClassName={s.membersModalBody}
        >
          <div className={s.membersChatName}>{membersChatTitle}</div>
          {membersLoading ? (
            <div className={s.card}>Загрузка…</div>
          ) : (
            <ul className={s.membersList}>
              {(membersList || []).map((m) => (
                <li key={m.id} className={s.membersItem}>
                  {m.name}
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}
    </>
  );
}

const ROLE_MENU_WIDTH = 170;
const ROLE_MENU_HEIGHT = 96;

function computeRoleMenuPosition(anchorRect) {
  const margin = 8;
  let top = anchorRect.bottom + 6;
  let left = anchorRect.left;

  if (top + ROLE_MENU_HEIGHT > window.innerHeight - margin) {
    top = Math.max(margin, anchorRect.top - ROLE_MENU_HEIGHT - 6);
  }
  if (left + ROLE_MENU_WIDTH > window.innerWidth - margin) {
    left = Math.max(margin, window.innerWidth - ROLE_MENU_WIDTH - margin);
  }
  return { top, left };
}

function ParticipantRoleMenuPortal({
  participant,
  position,
  editChatId,
  onClose,
  onToggleChatAdmin,
}) {
  if (!participant || !position || typeof document === 'undefined') return null;

  return createPortal(
    <>
      <div
        className={s.tagMenuBackdrop}
        onClick={onClose}
        onTouchStart={onClose}
        aria-hidden="true"
      />
      <ul
        className={`${s.tagMenu} ${s.tagMenuFloating}`}
        style={{ top: position.top, left: position.left }}
        role="menu"
      >
        <li
          role="menuitem"
          className={participant.chatAdmin ? s.activeItem : ''}
          onClick={async () => {
            onClose();
            if (!participant.chatAdmin) {
              if (!window.confirm(`«${participant.name}» будет админом этого чата?`)) return;
            }
            await onToggleChatAdmin?.(editChatId, participant.id, true);
          }}
        >
          Админ чата
        </li>
        <li
          role="menuitem"
          className={!participant.chatAdmin ? s.activeItem : ''}
          onClick={async () => {
            onClose();
            await onToggleChatAdmin?.(editChatId, participant.id, false);
          }}
        >
          Участник
        </li>
      </ul>
    </>,
    document.body
  );
}

function dedupeById(list) {
  const seen = new Set();
  return (list || []).filter((p) => {
    const k = String(p.id);
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
}

function ChatParticipantPicker({
  selected = [],
  onChange,
  excludeEmployeeId = null,
  currentEmployeeId = null,
  resolveImageUrl,
  mode = 'create',
  editChatId = null,
  roleMenuFor = null,
  setRoleMenuFor,
  onToggleChatAdmin,
}) {
  const [employeesBase, setEmployeesBase] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [listOpen, setListOpen] = useState(false);
  const [roleMenuPosition, setRoleMenuPosition] = useState(null);
  const pickerDropdownRef = React.useRef(null);

  const closeRoleMenu = useCallback(() => {
    setRoleMenuPosition(null);
    setRoleMenuFor?.(null);
  }, [setRoleMenuFor]);

  const openRoleMenu = useCallback((participantId, anchorEl) => {
    if (!anchorEl) return;
    if (roleMenuFor === participantId) {
      closeRoleMenu();
      return;
    }
    const rect = anchorEl.getBoundingClientRect();
    setRoleMenuPosition(computeRoleMenuPosition(rect));
    setRoleMenuFor?.(participantId);
  }, [roleMenuFor, setRoleMenuFor, closeRoleMenu]);

  useEffect(() => {
    if (roleMenuFor == null) {
      setRoleMenuPosition(null);
    }
  }, [roleMenuFor]);

  useEffect(() => {
    if (roleMenuFor == null) return undefined;
    const onViewportChange = () => closeRoleMenu();
    window.addEventListener('resize', onViewportChange);
    window.addEventListener('scroll', onViewportChange, true);
    return () => {
      window.removeEventListener('resize', onViewportChange);
      window.removeEventListener('scroll', onViewportChange, true);
    };
  }, [roleMenuFor, closeRoleMenu]);

  const handleSearchBlur = useCallback(() => {
    window.setTimeout(() => {
      if (!pickerDropdownRef.current?.contains(document.activeElement)) {
        setListOpen(false);
      }
    }, 0);
  }, []);

  useEffect(() => {
    if (!listOpen) return undefined;
    const onOutsidePointer = (e) => {
      if (!pickerDropdownRef.current?.contains(e.target)) {
        setListOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutsidePointer);
    document.addEventListener('touchstart', onOutsidePointer);
    return () => {
      document.removeEventListener('mousedown', onOutsidePointer);
      document.removeEventListener('touchstart', onOutsidePointer);
    };
  }, [listOpen]);

  // При создании чата себя в списке не показываем (добавит бэкенд). В редактировании — показываем.
  const hideSelfFromList = mode === 'create' && excludeEmployeeId != null;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await apiFetch(`${API_URL}/employees/search/?query=`);
        const data = response.ok ? await response.json() : [];
        let arr = Array.isArray(data) ? data : [];
        if (hideSelfFromList) {
          arr = arr.filter((e) => String(e.id) !== String(excludeEmployeeId));
        }
        if (!cancelled) setEmployeesBase(arr);
      } catch {
        if (!cancelled) setEmployeesBase([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [excludeEmployeeId, hideSelfFromList]);

  const employees = useMemo(() => {
    let arr = [...employeesBase];
    if (mode === 'edit') {
      const byId = new Map(arr.map((e) => [String(e.id), e]));
      (selected || []).forEach((p) => {
        const k = String(p.id);
        if (!byId.has(k)) {
          byId.set(k, {
            id: p.id,
            name: p.name,
            role: p.role || null,
            avatar: p.avatar || null,
            avatar_url: p.avatar || null,
          });
        }
      });
      arr = Array.from(byId.values());
    }
    arr.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'));
    return arr;
  }, [employeesBase, mode, selected]);

  const selectedUnique = useMemo(() => dedupeById(selected), [selected]);

  const roleMenuParticipant = useMemo(() => {
    if (roleMenuFor == null) return null;
    return selectedUnique.find((p) => String(p.id) === String(roleMenuFor)) || null;
  }, [roleMenuFor, selectedUnique]);

  const selectedById = useMemo(() => {
    const map = new Map();
    selectedUnique.forEach((p) => map.set(String(p.id), p));
    return map;
  }, [selectedUnique]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return employees;
    return employees.filter((e) => (e.name || '').toLowerCase().includes(q));
  }, [employees, search]);

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((e) => selectedById.has(String(e.id)));

  const toggleOne = (emp) => {
    const key = String(emp.id);
    if (selectedById.has(key)) {
      onChange((selected || []).filter((p) => String(p.id) !== key));
      return;
    }
    const existing = selectedById.get(key);
    onChange([
      ...(selected || []),
      existing || {
        id: emp.id,
        name: emp.name,
        role: emp.role || null,
        avatar: emp.avatar || emp.avatar_url || null,
        chatAdmin: false,
      },
    ]);
  };

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const visibleIds = new Set(filtered.map((e) => String(e.id)));
      const selfKey = currentEmployeeId != null ? String(currentEmployeeId) : null;
      onChange((selected || []).filter((p) => {
        const key = String(p.id);
        if (selfKey && key === selfKey) return true;
        return !visibleIds.has(key);
      }));
      return;
    }
    const next = [...(selected || [])];
    const ids = new Set(next.map((p) => String(p.id)));
    filtered.forEach((emp) => {
      const key = String(emp.id);
      if (ids.has(key)) return;
      const existing = selectedById.get(key);
      next.push(
        existing || {
          id: emp.id,
          name: emp.name,
          role: emp.role || null,
          avatar: emp.avatar || emp.avatar_url || null,
          chatAdmin: false,
        }
      );
      ids.add(key);
    });
    onChange(next);
  };

  const removeSelected = (id) => {
    onChange((selected || []).filter((p) => String(p.id) !== String(id)));
  };

  const isEdit = mode === 'edit';

  return (
    <div className={s.participantPicker}>
      {selectedUnique.length > 0 && (
        <div className={s.participantPickerTags}>
          <div className={s.selectedTags}>
            {selectedUnique.map((p) => (
              <span key={p.id} className={s.tag}>
                {isEdit && editChatId && (
                  <button
                    type="button"
                    className={s.tagEdit}
                    title="Изменить права: админ или участник"
                    onClick={(e) => {
                      e.stopPropagation();
                      openRoleMenu(p.id, e.currentTarget);
                    }}
                    aria-label={`Права для ${p.name}`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21 20.9998H13M2.5 21.4998L8.04927 19.3655C8.40421 19.229 8.58168 19.1607 8.74772 19.0716C8.8952 18.9924 9.0358 18.901 9.16804 18.7984C9.31692 18.6829 9.45137 18.5484 9.72028 18.2795L21 6.99982C22.1046 5.89525 22.1046 4.10438 21 2.99981C19.8955 1.89525 18.1046 1.89524 17 2.99981L5.72028 14.2795C5.45138 14.5484 5.31692 14.6829 5.20139 14.8318C5.09877 14.964 5.0074 15.1046 4.92823 15.2521C4.83911 15.4181 4.77085 15.5956 4.63433 15.9506L2.5 21.4998ZM2.5 21.4998L4.55812 16.1488C4.7054 15.7659 4.77903 15.5744 4.90534 15.4867C5.01572 15.4101 5.1523 15.3811 5.2843 15.4063C5.43533 15.4351 5.58038 15.5802 5.87048 15.8703L8.12957 18.1294C8.41967 18.4195 8.56472 18.5645 8.59356 18.7155C8.61877 18.8475 8.58979 18.9841 8.51314 19.0945C8.42545 19.2208 8.23399 19.2944 7.85107 19.4417L2.5 21.4998Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                  </button>
                )}

                <span className={s.tagName}>
                  {p.name}
                  {p.role ? <span className={s.roleBadge}> ({p.role})</span> : null}
                  {isEdit && p.chatAdmin ? (
                    <span className={s.roleBadge}>админ</span>
                  ) : null}
                </span>

                <button
                  type="button"
                  className={s.tagRemove}
                  onClick={() => removeSelected(p.id)}
                  aria-label={`Убрать ${p.name}`}
                >
                  <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                    <path d="M2 2l8 8M10 2l-8 8" />
                  </svg>
                </button>

              </span>
            ))}
          </div>
        </div>
      )}

      <ParticipantRoleMenuPortal
        participant={roleMenuParticipant}
        position={roleMenuPosition}
        editChatId={editChatId}
        onClose={closeRoleMenu}
        onToggleChatAdmin={onToggleChatAdmin}
      />

      <div ref={pickerDropdownRef} className={s.participantPickerDropdown}>
        <div className={s.participantPickerSearchWrap}>
          <SearchInput
            className={s.participantPickerSearchField}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => setListOpen(true)}
            onBlur={handleSearchBlur}
            placeholder="Поиск сотрудников"
            ariaLabel="Поиск сотрудников"
          />
        </div>

        {listOpen && (
          <div
            className={s.participantPickerList}
            role="listbox"
            aria-multiselectable="true"
            onMouseDown={(e) => e.preventDefault()}
          >
            {loading && (
              <div className={s.participantPickerEmpty}>Загрузка…</div>
            )}

            {!loading && filtered.length === 0 && (
              <div className={s.participantPickerEmpty}>
                {search.trim() ? 'Никого не найдено' : 'Сотрудники не найдены'}
              </div>
            )}

            {!loading && filtered.length > 0 && (
              <>
                <label className={`${s.participantPickerRow} ${s.participantPickerRowAll}`}>
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    aria-label="Выбрать всех"
                  />
                  <span className={s.participantPickerName}>
                    <strong>Выбрать всех</strong>
                  </span>
                </label>

                {filtered.map((emp) => {
              const key = String(emp.id);
              const picked = selectedById.get(key);
              const checked = !!picked;
              return (
                <div key={emp.id} className={s.participantPickerRowWrap}>
                  <label className={s.participantPickerRow}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleOne(emp)}
                    />
                    <Avatar
                      src={resolveImageUrl(emp.avatar || emp.avatar_url)}
                      name={emp.name}
                      alt={emp.name || ''}
                      size={40}
                      className={s.participantPickerAvatar}
                    />
                    <span className={s.participantPickerName}>
                      {emp.name}
                      {currentEmployeeId != null && String(emp.id) === String(currentEmployeeId) ? (
                        <span className={s.roleBadge}> (вы)</span>
                      ) : null}
                      {emp.role ? (
                        <span className={s.roleBadge}> ({emp.role})</span>
                      ) : null}
                      {isEdit && picked?.chatAdmin ? (
                        <span className={s.roleBadge}> · админ</span>
                      ) : null}
                    </span>
                  </label>

                  {isEdit && checked && editChatId && (
                    <div className={s.participantPickerAdmin}>
                      <button
                        type="button"
                        className={s.participantPickerAdminBtn}
                        title="Права участника"
                        aria-label={`Права для ${emp.name}`}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openRoleMenu(picked.id, e.currentTarget);
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M21 20.9998H13M2.5 21.4998L8.04927 19.3655C8.40421 19.229 8.58168 19.1607 8.74772 19.0716C8.8952 18.9924 9.0358 18.901 9.16804 18.7984C9.31692 18.6829 9.45137 18.5484 9.72028 18.2795L21 6.99982C22.1046 5.89525 22.1046 4.10438 21 2.99981C19.8955 1.89525 18.1046 1.89524 17 2.99981L5.72028 14.2795C5.45138 14.5484 5.31692 14.6829 5.20139 14.8318C5.09877 14.964 5.0074 15.1046 4.92823 15.2521C4.83911 15.4181 4.77085 15.5956 4.63433 15.9506L2.5 21.4998ZM2.5 21.4998L4.55812 16.1488C4.7054 15.7659 4.77903 15.5744 4.90534 15.4867C5.01572 15.4101 5.1523 15.3811 5.2843 15.4063C5.43533 15.4351 5.58038 15.5802 5.87048 15.8703L8.12957 18.1294C8.41967 18.4195 8.56472 18.5645 8.59356 18.7155C8.61877 18.8475 8.58979 18.9841 8.51314 19.0945C8.42545 19.2208 8.23399 19.2944 7.85107 19.4417L2.5 21.4998Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              );
                })}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
